import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Grid,
  IconButton,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  Switch,
} from "@mui/material";
import { Fragment } from "react";
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTracingCrud, getSubscriptionDefaultFrequency } from "./hooks";
import type { DataRow, DataRowDetail } from "./types";
import GenericDialog from "../../shared/components/GenericDialog";
import WizardDialog, { type WizardStep } from "../../shared/components/WizardDialog";
import SearchInput from "../../shared/components/SearchInput";

const TracingCrud: React.FC = () => {
  const {
    filteredTableData,
    selectedClient,
    selectedClientId,
    expandedClientId,
    customerSearch,
    setCustomerSearch,
    availableServices,
    filteredAvailableServices,
    isAddServiceOpen,
    addServiceSearch,
    setAddServiceSearch,
    frequencyOverrides,
    isEditFrequencyOpen,
    editFrequencyValue,
    editFinishDate,
    editInactive,
    setEditFrequencyValue,
    setEditFinishDate,
    isUpdatingFrequency,
    isAddingService,
    handleSelectClient,
    handleToggleExpand,
    handleFrequencyChange,
    handleOpenAddServiceForClient,
    handleCloseAddService,
    handleAddService,
    handleOpenEditFrequency,
    handleCloseEditFrequency,
    handleSaveFrequency,
    handleInactiveChange,
    // Diálogo unificado de suscripciones
    availableCustomers,
    isSubscriptionDialogOpen,
    isConfirmingSubscriptions,
    subscriptionSelectedCustomers,
    subscriptionSelectedServices,
    subscriptionFrequencies,
    subscriptionCustomerSearch,
    subscriptionNewServices,
    subscriptionSelectedCustomerCount,
    subscriptionSelectedServiceCount,
    subscriptionValidNewServicesCount,
    subscriptionFilteredCustomers,
    subscriptionActiveServices,
    subscriptionFilteredServices,
    subscriptionServiceSearch,
    subscriptionCanConfirm,
    subscriptionNewPairsCount,
    subscriptionSkippedPairsCount,
    setSubscriptionCustomerSearch,
    setSubscriptionServiceSearch,
    handleToggleSubscriptionCustomer,
    handleToggleAllSubscriptionCustomers,
    handleToggleSubscriptionService,
    handleToggleAllSubscriptionServices,
    handleSubscriptionFrequencyChange,
    handleAddSubscriptionNewService,
    handleRemoveSubscriptionNewService,
    handleUpdateSubscriptionNewService,
    handleOpenSubscriptionDialog,
    handleCloseSubscriptionDialog,
    handleConfirmSubscriptions,
  } = useTracingCrud();

  const theme = useTheme();

  const subscriptionSteps: WizardStep[] = [
    {
      label: "Crear Servicios",
      content: (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Creá nuevos servicios (opcional). Estos se agregarán a la lista de servicios disponibles.
          </Typography>

          {subscriptionNewServices.map((entry) => (
            <Paper key={entry.tempId} elevation={1} sx={{ p: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                <TextField
                  label="Nombre"
                  size="small"
                  value={entry.name}
                  onChange={(e) => handleUpdateSubscriptionNewService(entry.tempId, 'name', e.target.value)}
                  sx={{ flex: { sm: 2 } }}
                  required
                />
                <TextField
                  label="Descripción"
                  size="small"
                  value={entry.description}
                  onChange={(e) => handleUpdateSubscriptionNewService(entry.tempId, 'description', e.target.value)}
                  sx={{ flex: { sm: 2 } }}
                />
                <TextField
                  label="Frecuencia (min)"
                  type="number"
                  size="small"
                  value={entry.frequency}
                  onChange={(e) => handleUpdateSubscriptionNewService(entry.tempId, 'frequency', Number(e.target.value))}
                  inputProps={{ min: 1 }}
                  sx={{ width: { xs: '100%', sm: '8.125rem' } }}
                />
                <IconButton
                  color="error"
                  onClick={() => handleRemoveSubscriptionNewService(entry.tempId)}
                  sx={{ mt: { xs: 0, sm: 0.5 }, alignSelf: { xs: 'flex-end', sm: 'auto' } }}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </Paper>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddSubscriptionNewService}
            sx={{ alignSelf: 'flex-start' }}
          >
            Agregar servicio
          </Button>
        </Stack>
      ),
    },
    {
      label: "Clientes y Servicios",
      content: (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Clientes ({subscriptionSelectedCustomerCount} seleccionados)
              </Typography>
              <SearchInput
                label="Buscar cliente"
                size="small"
                fullWidth
                value={subscriptionCustomerSearch}
                onChange={setSubscriptionCustomerSearch}
                sx={{ mb: 1 }}
              />
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={
                            subscriptionSelectedCustomerCount > 0 &&
                            subscriptionSelectedCustomerCount < subscriptionFilteredCustomers.length
                          }
                          checked={
                            subscriptionFilteredCustomers.length > 0 &&
                            subscriptionFilteredCustomers.every(c => subscriptionSelectedCustomers[c.id])
                          }
                          onChange={handleToggleAllSubscriptionCustomers}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subscriptionFilteredCustomers.map(customer => (
                      <TableRow
                        key={`sub-customer-${customer.id}`}
                        hover
                        onClick={() => handleToggleSubscriptionCustomer(customer.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox checked={!!subscriptionSelectedCustomers[customer.id]} />
                        </TableCell>
                        <TableCell>{customer.id}</TableCell>
                        <TableCell>{customer.name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Servicios ({subscriptionSelectedServiceCount + subscriptionValidNewServicesCount} seleccionados)
              </Typography>
              <SearchInput
                label="Buscar servicio"
                size="small"
                fullWidth
                value={subscriptionServiceSearch}
                onChange={setSubscriptionServiceSearch}
                sx={{ mb: 1 }}
              />
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={
                            subscriptionSelectedServiceCount > 0 &&
                            subscriptionSelectedServiceCount < subscriptionFilteredServices.length
                          }
                          checked={
                            subscriptionFilteredServices.length > 0 &&
                            subscriptionFilteredServices.every(s => subscriptionSelectedServices[s.id])
                          }
                          onChange={handleToggleAllSubscriptionServices}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Servicio</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Frecuencia</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subscriptionNewServices.filter(s => s.name.trim()).map(entry => (
                      <TableRow key={`new-svc-${entry.tempId}`} sx={{ backgroundColor: 'rgba(25, 118, 210, 0.08)' }}>
                        <TableCell padding="checkbox">
                          <Checkbox checked disabled />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2">{entry.name}</Typography>
                            <Chip label="Nuevo" size="small" color="primary" />
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={subscriptionFrequencies[entry.tempId] ?? entry.frequency}
                            onChange={(e) => handleSubscriptionFrequencyChange(entry.tempId, Number(e.target.value))}
                            inputProps={{ min: 1 }}
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {subscriptionFilteredServices.map(service => (
                      <TableRow
                        key={`sub-service-${service.id}`}
                        hover
                        onClick={() => handleToggleSubscriptionService(service.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox checked={!!subscriptionSelectedServices[service.id]} />
                        </TableCell>
                        <TableCell>{service.id} - {service.name}</TableCell>
                        <TableCell>
                          {subscriptionSelectedServices[service.id] ? (
                            <TextField
                              type="number"
                              size="small"
                              value={subscriptionFrequencies[service.id] ?? getSubscriptionDefaultFrequency(service)}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleSubscriptionFrequencyChange(service.id, Number(e.target.value));
                              }}
                              onClick={(e) => e.stopPropagation()}
                              inputProps={{ min: 1 }}
                              sx={{ width: 80 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {getSubscriptionDefaultFrequency(service)} min
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      ),
    },
    {
      label: "Resumen",
      content: (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Revisá las asignaciones antes de confirmar.
          </Typography>

          {subscriptionValidNewServicesCount > 0 && (
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Servicios a crear
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {subscriptionNewServices.filter(s => s.name.trim()).map(entry => (
                  <Chip
                    key={entry.tempId}
                    label={`${entry.name} (${subscriptionFrequencies[entry.tempId] ?? entry.frequency} min)`}
                    color="primary"
                    size="small"
                  />
                ))}
              </Stack>
            </Paper>
          )}

          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Clientes seleccionados ({subscriptionSelectedCustomerCount})
            </Typography>
            {subscriptionSelectedCustomerCount === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No hay clientes seleccionados.
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {availableCustomers
                  .filter(c => subscriptionSelectedCustomers[c.id])
                  .map(customer => (
                    <Chip key={customer.id} label={customer.name} size="small" sx={{ mb: 0.5 }} />
                  ))}
              </Stack>
            )}
          </Paper>

          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Servicios a asignar ({subscriptionSelectedServiceCount + subscriptionValidNewServicesCount})
            </Typography>
            {subscriptionSelectedServiceCount === 0 && subscriptionValidNewServicesCount === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No hay servicios seleccionados.
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {subscriptionNewServices.filter(s => s.name.trim()).map(entry => (
                  <Chip
                    key={entry.tempId}
                    label={`${entry.name} (${subscriptionFrequencies[entry.tempId] ?? entry.frequency} min)`}
                    color="primary"
                    size="small"
                    sx={{ mb: 0.5 }}
                  />
                ))}
                {subscriptionActiveServices
                  .filter(s => subscriptionSelectedServices[s.id])
                  .map(service => (
                    <Chip
                      key={service.id}
                      label={`${service.name} (${subscriptionFrequencies[service.id] ?? getSubscriptionDefaultFrequency(service)} min)`}
                      size="small"
                      sx={{ mb: 0.5 }}
                    />
                  ))}
              </Stack>
            )}
          </Paper>

          {subscriptionCanConfirm && (
            <Paper elevation={1} sx={{ p: 2, backgroundColor: theme.palette.success.main }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                Se crearán {subscriptionNewPairsCount} suscripcion{subscriptionNewPairsCount === 1 ? '' : 'es'}
              </Typography>
              {subscriptionSkippedPairsCount > 0 && (
                <Typography variant="body2" sx={{ color: 'white' }}>
                  {subscriptionSkippedPairsCount} combinación{subscriptionSkippedPairsCount === 1 ? '' : 'es'} ya existente{subscriptionSkippedPairsCount === 1 ? '' : 's'} serán ignoradas
                </Typography>
              )}
            </Paper>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Fragment>
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Gestión de Suscripciones
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creá servicios y asignalos a múltiples clientes en un solo paso.
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenSubscriptionDialog}
            sx={{ '&:focus': { outline: 'none', boxShadow: 'none' } }}
          >
            Nueva suscripción
          </Button>
        </Paper>

        <Paper elevation={2} sx={{ p: 2, mt: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Agregar servicio a cliente
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Seleccioná un cliente para agregarle un servicio y gestionar sus servicios activos.
          </Typography>

        <SearchInput
          label="Buscar cliente"
          placeholder="Cliente o empresa"
          value={customerSearch}
          onChange={setCustomerSearch}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        />

        {filteredTableData.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No hay clientes disponibles.
            </Typography>
          ) : (
            <TableContainer sx={{ flex: 1, overflow: 'auto', overflowX: 'auto', minHeight: 0 }}>
              <Table size="small" sx={{ minWidth: { sm: '28rem', md: 'auto' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40, p: 0 }} />
                    <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', sm: 'table-cell' } }}>Empresa</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>Servicios</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTableData.map((client: DataRow) => {
                    const activeServices = client.details.filter(service => !service.inactive);
                    const inactiveServices = client.details.filter(service => service.inactive);
                    const isExpanded = expandedClientId === client.id;
                    const isSelected = selectedClientId === client.id;

                    return (
                      <Fragment key={client.id}>
                        <TableRow
                          hover
                          selected={isSelected}
                          onClick={() => { handleSelectClient(client.id); handleToggleExpand(client.id); }}
                          sx={{
                            cursor: 'pointer',
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(16, 185, 129, 0.12)',
                              '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.18)' },
                            },
                          }}
                        >
                          <TableCell sx={{ p: 0, width: 40 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleExpand(client.id);
                              }}
                              sx={{
                                '&:focus': { outline: 'none', boxShadow: 'none' },
                                transition: 'transform 0.2s',
                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                              }}
                            >
                              <ChevronRightIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                          <TableCell>{client.customer}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{client.business}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography variant="body2" color="text.secondary">
                              {activeServices.length} activo{activeServices.length !== 1 ? 's' : ''}
                            </Typography>
                            {inactiveServices.length > 0 && (
                              <Typography variant="caption" color="text.disabled">
                                {inactiveServices.length} inactivo{inactiveServices.length !== 1 ? 's' : ''}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenAddServiceForClient(client.id);
                              }}
                              sx={{
                                '&:focus': { outline: 'none', boxShadow: 'none' },
                                minWidth: 'auto',
                                px: { xs: 1, sm: 2 },
                                '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 }, ml: { xs: 0 } },
                              }}
                            >
                              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                Agregar servicio
                              </Box>
                            </Button>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={5} sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 1, px: 2, pl: { xs: 2, sm: 6 }, backgroundColor: theme.palette.grey[50] }}>
                                {client.details.length === 0 ? (
                                  <Typography variant="caption" color="text.secondary">
                                    Sin servicios.
                                  </Typography>
                                ) : (
                                  <Stack spacing={1}>
                                    {client.details.map((service: DataRowDetail) => (
                                      <Stack
                                        key={service.id}
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        onClick={() => handleOpenEditFrequency(service)}
                                        sx={{
                                          py: 0.5,
                                          px: 1,
                                          borderRadius: 1,
                                          opacity: service.inactive ? 0.55 : 1,
                                          cursor: { xs: 'pointer', sm: 'default' },
                                          '&:hover': { backgroundColor: theme.palette.grey[100] },
                                        }}
                                      >
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                          <Stack>
                                            <Typography variant="body2" color={service.inactive ? 'text.disabled' : 'text.primary'}>
                                              {service.service}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {service.frequency}
                                            </Typography>
                                          </Stack>
                                          {service.inactive && (
                                            <Chip label="Inactivo" size="small" variant="outlined" color="default" />
                                          )}
                                        </Stack>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={(e) => { e.stopPropagation(); handleOpenEditFrequency(service); }}
                                          sx={{ display: { xs: 'none', sm: 'flex' }, '&:focus': { outline: 'none', boxShadow: 'none' } }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Stack>
                                    ))}
                                  </Stack>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Dialog: Edición de frecuencia */}
      <GenericDialog
        open={isEditFrequencyOpen}
        onClose={handleCloseEditFrequency}
        title="Ajustar frecuencia del servicio"
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              onClick={handleCloseEditFrequency}
              size="small"
              variant="outlined"
              sx={{ '&:focus': { outline: 'none', boxShadow: 'none' } }}
            >
              Cerrar
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSaveFrequency}
              disabled={isUpdatingFrequency}
              sx={{ '&:focus': { outline: 'none', boxShadow: 'none' } }}
            >
              {isUpdatingFrequency ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </Stack>
        }
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Ajustá la frecuencia y la baja del servicio.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
            <TextField
              label="Frecuencia (min)"
              type="number"
              size="small"
              value={editFrequencyValue}
              onChange={(e) => setEditFrequencyValue(Number(e.target.value))}
              inputProps={{ min: 1 }}
              sx={{ width: { xs: '100%', sm: '10rem' } }}
            />
            <TextField
              label="Baja"
              type="date"
              size="small"
              value={editFinishDate}
              onChange={(e) => setEditFinishDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', sm: '11.25rem' } }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editInactive}
                  onChange={(e) => handleInactiveChange(e.target.checked)}
                />
              }
              label="Inactivo"
            />
          </Stack>
        </Stack>
      </GenericDialog>

      {/* Dialog: Agregar servicio */}
      <GenericDialog
        open={isAddServiceOpen}
        onClose={handleCloseAddService}
        title={`Agregar servicio${selectedClient ? ` - ${selectedClient.customer}` : ''}`}
        maxWidth="sm"
        actions={
          <Button
            onClick={handleCloseAddService}
            size="small"
            variant="outlined"
            sx={{ '&:focus': { outline: 'none', boxShadow: 'none' } }}
          >
            Cerrar
          </Button>
        }
      >
        <Stack spacing={2}>
          {/* Buscador */}
          <SearchInput
            label="Buscar servicio"
            placeholder="Nombre, descripción o ID"
            value={addServiceSearch}
            onChange={setAddServiceSearch}
            size="small"
            fullWidth
          />

          {/* Lista de servicios */}
          {filteredAvailableServices.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              {availableServices.length === 0
                ? 'No hay servicios disponibles para agregar.'
                : 'No se encontraron servicios con ese criterio.'}
            </Typography>
          ) : (
            <Stack spacing={1.5} sx={{ maxHeight: 400, overflow: 'auto', pr: 1 }}>
              {filteredAvailableServices.map(service => {
                const hasResults = (service.possibleResults ?? []).length > 0;
                const frequencyValue = frequencyOverrides[service.id] ?? service.defaultFrequency ?? 0;

                return (
                  <Paper
                    key={`available-service-${service.id}`}
                    elevation={1}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                    }}
                  >
                    {/* Header: ID chip + Nombre */}
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <Chip label={`ID: ${service.id}`} size="small" variant="outlined" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {service.name}
                      </Typography>
                    </Stack>

                    {/* Descripción */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 20 }}>
                      {service.description || 'Sin descripción'}
                    </Typography>

                    {/* Warning si no tiene resultados */}
                    {!hasResults && (
                      <Chip
                        label="Sin resultados posibles configurados"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ mb: 1.5 }}
                      />
                    )}

                    {/* Controles: Frecuencia + Botón */}
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <TextField
                        label="Frecuencia (min)"
                        type="number"
                        size="small"
                        value={frequencyValue}
                        onChange={(e) => handleFrequencyChange(service.id, e.target.value)}
                        inputProps={{ min: 1 }}
                        sx={{ width: 140 }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleAddService(service.id, frequencyValue)}
                        disabled={isAddingService || !hasResults}
                        sx={{ '&:focus': { outline: 'none', boxShadow: 'none' } }}
                      >
                        {isAddingService ? 'Agregando...' : 'Agregar'}
                      </Button>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Stack>
      </GenericDialog>

      {/* Dialog: Suscripciones unificado */}
      <WizardDialog
        open={isSubscriptionDialogOpen}
        onClose={handleCloseSubscriptionDialog}
        title="Gestión de Suscripciones"
        maxWidth="lg"
        steps={subscriptionSteps}
        onConfirm={handleConfirmSubscriptions}
        isConfirming={isConfirmingSubscriptions}
        canConfirm={subscriptionCanConfirm}
        disableFutureTabs
      />
    </Fragment>
  );
};

export default TracingCrud;
